import React, { useState, useEffect } from 'react';
import { Activity, Flame, Calendar, Gauge, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../supabase';
import { getSetVolume } from '../../utils/weightUtils';
import { calculateSessionCalories } from '../../services/calorieService';
import { colors as theme } from '../../theme';

const colors = {
  surface1: theme.bg,
  surface2: theme.surface1,
  surface3: theme.surface3,
  border: 'rgba(255, 255, 255, 0.08)',
  text: theme.text,
  textMuted: theme.textMuted,
  accent: theme.gold,
  effort: '#0A84FF',
  success: theme.success,
  error: theme.danger,
};

const typography = {
  h3: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  tiny: { fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' },
};

const styles = {
  card: { padding: '20px', backgroundColor: colors.surface1, borderRadius: '14px', border: `1px solid ${colors.border}` },
  titleIcon: {
    width: '30px', height: '30px', borderRadius: '8px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: `${colors.accent}1F`, color: colors.accent,
  },
  toggleWrap: { display: 'flex', gap: '8px' },
  toggleBtn: {
    padding: '7px 13px', minHeight: '44px', backgroundColor: 'transparent', border: `1px solid ${colors.border}`,
    borderRadius: '8px', color: colors.textMuted, fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
  },
  // Segmented-control selected state — translucent gold fill + thin gold
  // border + gold text, NOT a solid yellow button (explicit brief
  // requirement — the old version used a filled gold button here).
  toggleBtnActive: { backgroundColor: `${colors.accent}1A`, borderColor: colors.accent, color: colors.accent },
  summaryCard: {
    padding: '14px 16px', backgroundColor: colors.surface2, border: `1px solid ${colors.border}`,
    borderRadius: '14px', marginBottom: '18px',
  },
  divider: { width: '1px', alignSelf: 'stretch', backgroundColor: colors.border, flexShrink: 0 },
};

// Monday-anchored week bucket for the week/all-time modes.
const mondayOf = (dateInput) => {
  const d = new Date(dateInput);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
};

const enumerateWeeks = (fromDate, toDate) => {
  const weeks = [];
  let cur = mondayOf(fromDate);
  const end = mondayOf(toDate);
  while (cur <= end) {
    weeks.push(new Date(cur));
    cur = new Date(cur);
    cur.setDate(cur.getDate() + 7);
  }
  return weeks;
};

const enumerateMonths = (fromDate, toDate) => {
  const months = [];
  let cur = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
  const end = new Date(toDate.getFullYear(), toDate.getMonth(), 1);
  while (cur <= end) {
    months.push(new Date(cur));
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return months;
};

// Supabase/PostgREST caps an unpaginated select at a default row limit
// (commonly 1000) — fine for 'sessions' mode (≤30 sessions' worth of logs)
// but an account with real history can easily have 1000+ workout_logs rows
// across weeks/all-time, and an unpaginated fetch would silently truncate
// rather than error, dropping whichever rows sort last. Page through
// explicitly so this can never silently go missing again.
const fetchAllWorkoutLogs = async (sessionIds) => {
  const PAGE_SIZE = 1000;
  let all = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .from('workout_logs')
      .select('session_id, exercise_type, duration_seconds, calories, weight, reps, exercise_id')
      .in('session_id', sessionIds)
      .eq('completed', true)
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      console.error('[SessionResponseChart] workout_logs fetch failed:', error);
      break;
    }
    all = all.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return all;
};

// Readiness (energy_rating, captured pre-session) and effort (rpe, captured
// post-session) live on the same workout_sessions row, so one query gets
// both at matching per-session granularity. Calories are MET-based, from
// workout_logs, same formula the post-workout summary modal uses.
// UNCHANGED from the previous version of this file — same data source, same
// dedup/pagination/flag logic, just consumed by a new UI below.
//
// Three modes:
//  'sessions' — last 12 individual sessions (each row = one session)
//  'weeks'    — last 12 calendar weeks (each row = one week's average/total)
//  'alltime'  — full history, auto-bucketed weekly if that stays readable
//               (≤20 buckets), monthly otherwise
const getSessionResponseData = async (userId, mode) => {
  const empty = { sessions: [], avgEnergy: null, avgEffort: null, avgCalories: null, unitLabel: '', periodNoun: 'session' };

  let query = supabase
    .from('workout_sessions')
    .select('id, started_at, energy_rating, rpe')
    .eq('user_id', userId)
    .not('completed_at', 'is', null)
    .order('started_at', { ascending: false });

  if (mode === 'sessions') {
    // Headroom for the near-duplicate collapse below — sessions started
    // within ~30min of each other get merged, so we need more than 12 raw
    // rows to reliably still end up with 12 real sessions after that.
    query = query.limit(30);
  } else if (mode === 'weeks') {
    const start = new Date();
    start.setDate(start.getDate() - 12 * 7);
    query = query.gte('started_at', start.toISOString());
  }
  // 'alltime' — no extra filter, pull full history

  const { data: rawSessions } = await query;
  if (!rawSessions || rawSessions.length === 0) return empty;

  // Collapse sessions started within 30min of each other for the same user —
  // almost always a rapid retry/test iteration, not a second real workout.
  const DEDUP_WINDOW_MS = 30 * 60 * 1000;
  const chronological = [...rawSessions].reverse(); // oldest → newest
  const deduped = [];
  for (const s of chronological) {
    const prev = deduped[deduped.length - 1];
    if (prev && (new Date(s.started_at) - new Date(prev.started_at)) < DEDUP_WINDOW_MS) {
      deduped[deduped.length - 1] = s; // same cluster — keep the later attempt
    } else {
      deduped.push(s);
    }
  }

  const sessionIds = deduped.map(s => s.id);
  const [{ data: userData }, logs] = await Promise.all([
    supabase.from('users').select('bodyweight_kg').eq('auth_user_id', userId).single(),
    fetchAllWorkoutLogs(sessionIds),
  ]);
  const bodyweight = userData?.bodyweight_kg || 75;

  const exerciseIds = [...new Set((logs || []).map(l => l.exercise_id).filter(Boolean))];
  const { data: exercises } = exerciseIds.length > 0
    ? await supabase.from('exercises').select('id, name, is_bodyweight, is_assisted, bodyweight_multiplier').in('id', exerciseIds)
    : { data: [] };

  const logsBySession = new Map();
  (logs || []).forEach(log => {
    if (!logsBySession.has(log.session_id)) logsBySession.set(log.session_id, []);
    logsBySession.get(log.session_id).push(log);
  });

  const caloriesBySession = new Map(deduped.map(s => [
    s.id,
    calculateSessionCalories(logsBySession.get(s.id) || [], exercises || [], bodyweight, getSetVolume).estimated,
  ]));

  let rows;
  let unitLabel;
  let periodNoun;

  if (mode === 'sessions') {
    const windowed = deduped.slice(-12);

    // A test/coach account can easily log several real sessions minutes
    // apart on the same calendar day — a plain "Aug 2" label can't tell
    // those apart, so any day that repeats in this window gets a time
    // suffix on every occurrence, so the axis never shows the same label
    // twice for genuinely different sessions.
    const dayLabels = windowed.map(s => new Date(s.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    const dayCounts = dayLabels.reduce((acc, d) => { acc[d] = (acc[d] || 0) + 1; return acc; }, {});

    rows = windowed.map((s, i) => {
      const dayLabel = dayLabels[i];
      const label = dayCounts[dayLabel] > 1
        ? `${dayLabel} ${new Date(s.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
        : dayLabel;
      const energyRating = s.energy_rating != null ? Number(s.energy_rating) : null;
      return {
        date: label,
        fullDate: new Date(s.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        energyRating,
        rpe: s.rpe != null ? Number(s.rpe) : null,
        calories: caloriesBySession.get(s.id) || 0,
        flagged: s.energy_rating === 1 && s.rpe >= 8,
      };
    });
    unitLabel = `LAST ${rows.length} SESSIONS`;
    periodNoun = 'session';
  } else {
    // weeks / alltime — bucket into week or month periods, including empty
    // periods (so rest weeks/months show as real gaps, not a compressed
    // timeline that hides them).
    const earliest = new Date(deduped[0].started_at);
    const latest = new Date(deduped[deduped.length - 1].started_at);

    let periodStarts;
    let granularity;
    if (mode === 'weeks') {
      granularity = 'week';
      const start = new Date();
      start.setDate(start.getDate() - 11 * 7);
      periodStarts = enumerateWeeks(start, new Date());
    } else {
      const spanWeeks = Math.ceil((mondayOf(latest) - mondayOf(earliest)) / (7 * 24 * 60 * 60 * 1000)) + 1;
      granularity = spanWeeks > 20 ? 'month' : 'week';
      periodStarts = granularity === 'week' ? enumerateWeeks(earliest, latest) : enumerateMonths(earliest, latest);
    }

    const keyOf = (dateInput) => granularity === 'week'
      ? mondayOf(dateInput).toISOString()
      : new Date(new Date(dateInput).getFullYear(), new Date(dateInput).getMonth(), 1).toISOString();

    const buckets = new Map(periodStarts.map(p => [p.toISOString(), {
      energySum: 0, energyCount: 0, rpeSum: 0, rpeCount: 0, calories: 0, flaggedCount: 0,
    }]));

    deduped.forEach(s => {
      const bucket = buckets.get(keyOf(s.started_at));
      if (!bucket) return; // outside the requested range
      if (s.energy_rating != null) { bucket.energySum += Number(s.energy_rating); bucket.energyCount++; }
      if (s.rpe != null) { bucket.rpeSum += Number(s.rpe); bucket.rpeCount++; }
      bucket.calories += caloriesBySession.get(s.id) || 0;
      if (s.energy_rating === 1 && s.rpe >= 8) bucket.flaggedCount++;
    });

    rows = periodStarts.map(p => {
      const b = buckets.get(p.toISOString());
      const energyRating = b.energyCount > 0 ? b.energySum / b.energyCount : null;
      return {
        date: granularity === 'week'
          ? p.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : p.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        fullDate: granularity === 'week'
          ? `Week of ${p.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
          : p.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        energyRating,
        rpe: b.rpeCount > 0 ? b.rpeSum / b.rpeCount : null,
        calories: Math.round(b.calories),
        flagged: b.flaggedCount > 0,
      };
    });

    unitLabel = mode === 'weeks' ? `LAST ${rows.length} WEEKS` : `ALL TIME · ${granularity === 'week' ? 'WEEKLY' : 'MONTHLY'}`;
    periodNoun = granularity;
  }

  const withEnergy = rows.filter(s => s.energyRating != null);
  const withEffort = rows.filter(s => s.rpe != null);
  const withCalories = rows.filter(s => s.calories > 0);

  return {
    sessions: rows,
    avgEnergy: withEnergy.length > 0 ? withEnergy.reduce((sum, s) => sum + s.energyRating, 0) / withEnergy.length : null,
    avgEffort: withEffort.length > 0 ? withEffort.reduce((sum, s) => sum + s.rpe, 0) / withEffort.length : null,
    avgCalories: withCalories.length > 0 ? Math.round(withCalories.reduce((sum, s) => sum + s.calories, 0) / withCalories.length) : null,
    unitLabel,
    periodNoun,
  };
};

// Smooth "S-curve" bezier through a set of points — horizontal-tangent
// control points at the midpoint x between each pair avoid overshoot, a
// standard technique for a gentle line without a charting library.
const buildSmoothPath = (points) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x},${points[0].y}`;
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const cx = p0.x + (p1.x - p0.x) / 2;
    d += ` C ${cx},${p0.y} ${cx},${p1.y} ${p1.x},${p1.y}`;
  }
  return d;
};

// energyRating is a raw 1-3 average (per-session it's always a clean
// integer; in weeks/alltime mode it can be fractional, so round to the
// nearest bucket same as the rest of the app already does elsewhere).
const READINESS_LETTER = ['L', 'M', 'H'];
const READINESS_WORD = ['Low', 'Medium', 'High'];
const readinessTier = (energyRating) => Math.max(1, Math.min(3, Math.round(energyRating))) - 1;

const CHART_W = 360;
const CHART_H = 206;
const PAD_LEFT = 30;
const PAD_RIGHT = 12;
const EFFORT_TOP = 18;
const EFFORT_BOTTOM = 132;
const READINESS_Y = 160;
const DATE_LABEL_Y = 192;
// The whole scale: Easy is saved as 2, and older sessions hold 5, 7 and 9. A floor of 4 drew Easy at 4
// (Codex's review of the client's Stats, task 017, M2)
const EFFORT_MIN = 1;
const EFFORT_MAX = 10;

export const yForEffort = (v) => {
  const clamped = Math.max(EFFORT_MIN, Math.min(EFFORT_MAX, v));
  return EFFORT_BOTTOM - ((clamped - EFFORT_MIN) / (EFFORT_MAX - EFFORT_MIN)) * (EFFORT_BOTTOM - EFFORT_TOP);
};

// Anchor date labels — first, last, and evenly-spaced ones in between so 12
// sessions never render 12 overlapping labels (brief: "Jul 3 / Jul 11 /
// Jul 22 / Aug 7 / Aug 14", not all 12 dates). Always includes the most
// recent session's date since that's the one people orient against.
const anchorIndices = (n, count = 5) => {
  if (n <= 1) return new Set([0]);
  const set = new Set();
  for (let i = 0; i < count; i++) set.add(Math.round((i * (n - 1)) / (count - 1)));
  return set;
};

/**
 * ONE integrated visualization — an Effort (RPE) line chart directly above a
 * compact Readiness marker strip, sharing the exact same x-position per
 * session so a reader can look straight down from an RPE point to see that
 * session's readiness. Replaces the previous two-separate-stacked-charts
 * design per the "premium coaching intelligence component" brief — the
 * whole point this time is that the two rows read as one relationship, not
 * two independent charts that happen to be near each other.
 *
 * Tapping either an RPE point or a readiness marker selects that session:
 * both markers pop (radius + glow), and a vertical connector line runs
 * from the RPE point through the readiness marker down toward the date —
 * "these two numbers belong to the same session." Selection state is owned
 * by the parent so the detail/insight cards below can read it too.
 */
const IntegratedSessionChart = ({ sessions, selectedIndex, onSelect }) => {
  const plottable = sessions.filter((s) => s.energyRating != null || s.rpe != null);
  if (plottable.length === 0) return null;

  const n = plottable.length;
  const plotW = CHART_W - PAD_LEFT - PAD_RIGHT;
  const xFor = (i) => PAD_LEFT + (n > 1 ? (plotW * i) / (n - 1) : plotW / 2);
  const labels = anchorIndices(n);

  const effortPoints = plottable
    .map((s, i) => (s.rpe != null ? { x: xFor(i), y: yForEffort(s.rpe), s, i } : null))
    .filter(Boolean);
  const effortPath = buildSmoothPath(effortPoints);

  const selected = selectedIndex != null ? plottable[selectedIndex] : null;

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      style={{ width: '100%', aspectRatio: `${CHART_W} / ${CHART_H}`, display: 'block' }}
    >
      {/* Subtle translucent red column behind a flagged session — reserved
          exclusively for high-demand sessions, never a general highlight. */}
      {plottable.map((s, i) => s.flagged && (
        <rect
          key={`flagbg-${i}`}
          x={xFor(i) - 14} y={EFFORT_TOP - 4}
          width={28} height={DATE_LABEL_Y - EFFORT_TOP}
          fill={colors.error} opacity={0.06} rx={4}
        />
      ))}

      {/* Effort axis — extremely subtle dashed horizontal lines only, no
          vertical gridline per session (brief explicitly rules this out). */}
      {[10, 8, 6, 4, 2].map((v) => (
        <g key={v}>
          <line
            x1={PAD_LEFT} x2={CHART_W - PAD_RIGHT} y1={yForEffort(v)} y2={yForEffort(v)}
            stroke={colors.border} strokeWidth={1} strokeDasharray="2 4"
          />
          <text x={PAD_LEFT - 6} y={yForEffort(v) + 3} fill={colors.textMuted} fontSize={9} textAnchor="end">{v}</text>
        </g>
      ))}

      {/* Selection connector — RPE point → readiness marker → date. Drawn
          before the markers so the circles sit cleanly on top of the line
          ends rather than the line poking through them. */}
      {selected && (
        <line
          x1={xFor(selectedIndex)} x2={xFor(selectedIndex)}
          y1={EFFORT_TOP - 2} y2={DATE_LABEL_Y - 10}
          stroke={selected.flagged ? colors.error : colors.text}
          strokeOpacity={selected.flagged ? 0.55 : 0.25}
          strokeWidth={1.5} strokeDasharray="3 3"
        />
      )}

      {/* Effort line + markers */}
      {effortPath && <path d={effortPath} fill="none" stroke={colors.effort} strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" />}
      {effortPoints.map(({ x, y, s, i }) => {
        const isSelected = i === selectedIndex;
        const isLatest = i === n - 1;
        const showValue = isSelected || s.flagged || isLatest;
        const markerColor = s.flagged ? colors.error : colors.effort;
        const r = isSelected || s.flagged ? 6 : 4;
        return (
          <g key={`e-${i}`} onClick={() => onSelect(i)} style={{ cursor: 'pointer' }}>
            <circle cx={x} cy={y} r={16} fill="transparent" />
            {isSelected && <circle cx={x} cy={y} r={r + 5} fill={markerColor} opacity={0.18} />}
            <circle cx={x} cy={y} r={r} fill={markerColor} stroke={colors.surface2} strokeWidth={1.5} />
            {showValue && (
              <g>
                {s.flagged && (
                  <rect x={x - 15} y={y - r - 20} width={30} height={16} rx={5} fill={`${colors.error}26`} stroke={`${colors.error}55`} strokeWidth={1} />
                )}
                <text x={x} y={y - r - 8} fill={markerColor} fontSize={10} fontWeight="700" textAnchor="middle">
                  {Number(s.rpe).toFixed(1)}
                </text>
              </g>
            )}
          </g>
        );
      })}

      {/* Readiness strip — one categorical H/M/L marker per session, same x
          as its RPE point. No line graph, no permanent legend. */}
      {plottable.map((s, i) => {
        if (s.energyRating == null) return null;
        const x = xFor(i);
        const isSelected = i === selectedIndex;
        const color = s.flagged ? colors.error : colors.accent;
        const r = isSelected || s.flagged ? 11 : 9.5;
        return (
          <g key={`r-${i}`} onClick={() => onSelect(i)} style={{ cursor: 'pointer' }}>
            <circle cx={x} cy={READINESS_Y} r={16} fill="transparent" />
            {isSelected && <circle cx={x} cy={READINESS_Y} r={r + 5} fill={color} opacity={0.18} />}
            <circle cx={x} cy={READINESS_Y} r={r} fill={colors.surface2} stroke={color} strokeWidth={isSelected ? 2 : 1.5} />
            <text x={x} y={READINESS_Y + 3.5} fill={color} fontSize={10} fontWeight="700" textAnchor="middle">
              {READINESS_LETTER[readinessTier(s.energyRating)]}
            </text>
          </g>
        );
      })}

      {/* Date anchors — only a handful, always including the most recent. */}
      {plottable.map((s, i) => labels.has(i) && (
        <text
          key={`d-${i}`} x={xFor(i)} y={DATE_LABEL_Y}
          fill={i === selectedIndex ? colors.text : colors.textMuted}
          fontWeight={i === selectedIndex ? '700' : '400'}
          fontSize={10} textAnchor="middle"
        >
          {s.date}
        </text>
      ))}
    </svg>
  );
};

// Small colored ring + letter — same visual language as the chart's own
// readiness markers, reused in the detail card instead of a new icon.
const ReadinessGlyph = ({ tierLetter, color, size = 22 }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: size, height: size, borderRadius: '50%',
    border: `1.5px solid ${color}`, color, fontSize: '11px', fontWeight: '700', flexShrink: 0,
  }}>
    {tierLetter}
  </span>
);

// Selected-session detail card — the one place a tap's result actually
// lands, instead of a floating tooltip over the chart (explicitly ruled
// out by the brief).
const SelectedSessionCard = ({ session }) => {
  if (!session) return null;
  const tier = session.energyRating != null ? readinessTier(session.energyRating) : null;
  const readinessColor = session.flagged ? colors.error : colors.accent;

  return (
    <div style={{ ...styles.summaryCard, marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Calendar size={13} color={colors.effort} />
        <span style={{ ...typography.tiny, color: colors.text }}>{session.fullDate?.toUpperCase() || session.date.toUpperCase()}</span>
      </div>
      <div style={{ display: 'flex', gap: '18px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            {tier != null && <ReadinessGlyph tierLetter={READINESS_LETTER[tier]} color={readinessColor} size={16} />}
            <span style={{ fontSize: '11px', color: colors.textMuted }}>Readiness</span>
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: readinessColor }}>
            {tier != null ? READINESS_WORD[tier] : '—'}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <Gauge size={13} color={colors.effort} />
            <span style={{ fontSize: '11px', color: colors.textMuted }}>Effort (RPE)</span>
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: colors.effort }}>
            {session.rpe != null ? Number(session.rpe).toFixed(1) : '—'}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <Flame size={13} color={colors.success} />
            <span style={{ fontSize: '11px', color: colors.textMuted }}>Calories</span>
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: colors.success }}>
            {session.calories > 0 ? `${session.calories.toLocaleString()} kcal` : '—'}
          </div>
        </div>
      </div>
    </div>
  );
};

// New logic, not present anywhere else in the app — an interpretation of
// the readiness/effort relationship across the whole visible window, not
// just a single session. "Matched" = NOT flagged (the app's own existing
// high-demand rule), counted only over sessions that actually have both
// readiness and effort logged (can't judge a match without both signals).
// The 80%-match threshold for a positive vs cautionary framing is a new,
// reasonable-but-invented cutoff — flagged as an assumption in the
// implementation report, per the brief's own instruction not to silently
// invent business logic without disclosing it.
const CoachingInterpretation = ({ sessions, periodNoun }) => {
  const judged = sessions.filter((s) => s.energyRating != null && s.rpe != null);
  if (judged.length === 0) return null;

  const mismatched = judged.filter((s) => s.flagged).length;
  const matched = judged.length - mismatched;
  const matchRate = matched / judged.length;
  const isPositive = matchRate >= 0.8;
  const noun = judged.length === 1 ? periodNoun : `${periodNoun}s`;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 13px',
      backgroundColor: isPositive ? `${colors.success}10` : `${colors.error}0D`,
      border: `1px solid ${isPositive ? colors.success : colors.error}2E`, borderRadius: '8px',
    }}>
      {isPositive
        ? <CheckCircle2 size={16} color={colors.success} style={{ flexShrink: 0 }} />
        : <AlertTriangle size={16} color={colors.error} style={{ flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: colors.text }}>
          {isPositive ? 'Training demand looks well managed' : 'Training demand has been high recently'}
        </div>
        <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
          {isPositive
            ? `${matched} of your last ${judged.length} ${noun} matched your readiness.`
            : `${mismatched} of your last ${judged.length} ${noun} combined low readiness with high effort.`}
        </div>
      </div>
    </div>
  );
};

/**
 * Session Response — a coaching-intelligence read on readiness vs effort,
 * shared by the client's own Stats page and the coach's client Performance
 * tab. Self-contained: just pass the client's userId.
 *
 * `showInsight` is still accepted for backward compatibility with the
 * coach caller, but no longer changes behaviour — the redesigned detail/
 * high-demand/coaching-interpretation cards below are now shown to both
 * audiences unconditionally, since the whole point of this redesign is
 * that Cipher interprets the data rather than only showing it to coaches.
 *
 * `onSummary`, if passed, fires with { flaggedCount, totalSessions } every
 * time this chart's own data loads — lets a parent (e.g. the coach
 * Performance page's "Attention Required" section) surface a high-demand
 * count using this component's own already-fetched data, instead of
 * re-querying the same session/readiness/effort data a second time.
 */
// eslint-disable-next-line no-unused-vars
const SessionResponseChart = ({ userId, showInsight = false, onSummary }) => {
  const [data, setData] = useState({ sessions: [], avgEnergy: null, avgEffort: null, avgCalories: null, unitLabel: '', periodNoun: 'session' });
  const [mode, setMode] = useState('sessions');
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    if (!userId) return;
    getSessionResponseData(userId, mode).then(result => {
      setData(result);
      if (onSummary) {
        const flaggedCount = result.sessions.filter(s => s.flagged).length;
        onSummary({ flaggedCount, totalSessions: result.sessions.length });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode]);

  // Fresh dataset (mode switch or reload) — drop any stale selection so the
  // chart recomputes its own sensible default (most recent session) rather
  // than pointing at an index that may no longer exist or mean the same
  // thing in the new window.
  useEffect(() => {
    setSelectedIndex(null);
  }, [mode, data.sessions.length]);

  if (data.sessions.length === 0) return null;

  const plottable = data.sessions.filter((s) => s.energyRating != null || s.rpe != null);
  const effectiveIndex = selectedIndex != null ? selectedIndex : (plottable.length > 0 ? plottable.length - 1 : null);
  const selectedSession = effectiveIndex != null ? plottable[effectiveIndex] : null;

  const readinessTierAvg = data.avgEnergy != null ? readinessTier(data.avgEnergy) : null;

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={styles.titleIcon}><Activity size={15} strokeWidth={2.3} /></div>
          <div>
            <h3 style={{ ...typography.h3, color: colors.text, margin: 0 }}>Session Response</h3>
            <p style={{ fontSize: '12px', fontWeight: '500', color: colors.textMuted, margin: '2px 0 0 0' }}>
              How you arrive → How hard you train
            </p>
          </div>
        </div>
        <div style={styles.toggleWrap}>
          {[
            { value: 'sessions', label: 'Sessions' },
            { value: 'weeks', label: 'Weeks' },
            { value: 'alltime', label: 'All Time' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setMode(opt.value)}
              style={{ ...styles.toggleBtn, ...(mode === opt.value ? styles.toggleBtnActive : {}) }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ONE compact summary surface — replaces the old two-tile KPI row. */}
      <div style={styles.summaryCard}>
        <div style={{ ...typography.tiny, color: colors.textMuted, marginBottom: '10px' }}>{data.unitLabel}</div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: colors.accent }}>
              {readinessTierAvg != null ? READINESS_WORD[readinessTierAvg] : '—'}
            </div>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.4px', color: colors.textMuted, marginTop: '2px' }}>READINESS</div>
          </div>
          <div style={styles.divider} />
          <div style={{ flex: 1, minWidth: 0, paddingLeft: '14px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: colors.effort }}>
              {data.avgEffort != null ? data.avgEffort.toFixed(1) : '—'}
            </div>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.4px', color: colors.textMuted, marginTop: '2px' }}>AVG RPE</div>
          </div>
          <div style={styles.divider} />
          <div style={{ flex: 1, minWidth: 0, paddingLeft: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <Flame size={13} color={colors.success} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '16px', fontWeight: '700', color: colors.success }}>
                {data.avgCalories != null ? data.avgCalories.toLocaleString() : '—'}
              </span>
            </div>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.4px', color: colors.textMuted, marginTop: '2px' }}>AVG KCAL / {data.periodNoun.toUpperCase()}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ ...typography.tiny, color: colors.effort, fontWeight: '700' }}>EFFORT (RPE)</span>
        <span style={{ fontSize: '11px', color: colors.textMuted }}>Tap a point for details</span>
      </div>

      <IntegratedSessionChart
        sessions={data.sessions}
        selectedIndex={effectiveIndex}
        onSelect={setSelectedIndex}
      />

      <SelectedSessionCard session={selectedSession} />
      <CoachingInterpretation sessions={data.sessions} periodNoun={data.periodNoun} />
    </div>
  );
};

export default SessionResponseChart;
